import {mixins} from 'vue-class-component';
import JhiDataUtils from '@/shared/data/data-utils.service';
import AlertMixin from '@/shared/alert/alert.mixin';
import {Component, Inject, Prop, Ref, Watch} from 'vue-property-decorator';
import InscriptionService from '@/bo/pages/entities/inscription/inscription.service';
import ApplicationService from '@/shared/application.service';
import {IInscription, Inscription} from '@/shared/model/inscription.model';
import {email, maxLength, required, requiredIf, sameAs} from 'vuelidate/lib/validators';
import AutocompletionLocationService from '@/fo/components/autocompletion-location/autocompletion-location.service';
import {ExamSession} from '@/shared/model/exam-session.model';
import CaptchetatWidget from '@/fo/components/captchetat/captchetat-widget.vue';
import CaptchetatService from '@/fo/components/captchetat/captchetat.service';
import Vue2Filters from 'vue2-filters';
import ExamSessionService from '@/bo/pages/entities/exam-session/exam-session.service';
import {
  ERR_FORM_INVALID,
  ERR_INSCRIPTION_FAILED,
  MIN_AGE,
  MIN_BIRTH_DATE,
  PHOTO_IDENTITY_MIME_TYPES,
  CARD_IDENTITY_MIME_TYPES,
} from '@/constants';
import DateUtils from '@/shared/date/date-utils.service';
import {Country} from '@/shared/model/enumeration/country.model';
import {Nationality} from '@/shared/model/enumeration/nationality.model';
import {Gender} from '@/shared/model/enumeration/gender.model';

const telFormatIsValid = value => {
  if (typeof value === 'undefined' || value === null || value === '') {
    return true;
  }
  return /^[+]?(\d{1,15})$/.test(value);
};

const noSpecialCharacters = value => {
  if (typeof value === 'undefined' || value === null || value === '') {
    return true;
  }
  return /^([a-zA-ZàâäéèêëiïöôùüçÀÂÄÉÈÊËIÏÖÔÙÜÇ\-,' ]+)$/.test(value);
};

// date format must be dd/mm/yyyy
const dateFormatIsValid = value => {
  if (typeof value === 'undefined' || value === null || value === '') {
    return true;
  }
  return /^([0-2][0-9]|(3)[0-1])(\/)(((0)[0-9])|((1)[0-2]))(\/)\d{4}$/.test(value);
};

// user must have at least 16 at registration date (today date)
const haveMinAge = (value, vm) => {
  if (typeof value === 'undefined' || value === null || value === '') {
    return true;
  }
  // convert string dd/mm/yyyy to Date
  let dateParts = value.split('/');
  let valueDate = new Date(dateParts[2], dateParts[1] - 1, dateParts[0]);
  let ageDifMs = new Date().getTime() - valueDate.getTime();
  let ageDate = new Date(ageDifMs);
  if (Math.abs(ageDate.getUTCFullYear() - 1970) >= MIN_AGE) {
    return true;
  } else {
    return false;
  }
};

//user's birthDate must be at least constant MIN_BIRTH_DATE
const birthDateGreaterThan = value => {
  if (typeof value === 'undefined' || value === null || value === '') {
    return true;
  }
  // convert string dd/mm/yyyy to Date
  let dateParts = value.split('/');
  let valueDate = new Date(dateParts[2], dateParts[1] - 1, dateParts[0]);
  if (valueDate > MIN_BIRTH_DATE) {
    return true;
  } else {
    return false;
  }
};

// user must enter at least one phone number (phoneNumber or mobilePhoneNumber)
const atLeastOnePhoneNumber = (value, vm) => {
  if (
    (typeof vm.mobilePhoneNumber === 'undefined' || vm.mobilePhoneNumber === 'null' || vm.mobilePhoneNumber === '') &&
    (typeof vm.phoneNumber === 'undefined' || vm.phoneNumber === 'null' || vm.phoneNumber === '')
  ) {
    return false;
  } else {
    return true;
  }
};

// if country is 'France' then CP must be 5 digits
const frenchCpMustHave5Digits = (value, vm) => {
  if (typeof value === 'undefined' || value === null || value === '') {
    return true;
  }
  if (typeof vm.country !== 'undefined' && vm.country.toUpperCase() === Country.FRA.toUpperCase()) {
    return /^[0-9]{5}$/.test(value);
  } else {
    return true;
  }
};

@Component({
  mixins: [Vue2Filters.mixin],
  components: {
    CaptchetatWidget,
  },
  validations: {
    inscription: {
      gender: {
        required,
      },
      firstName: {
        required,
        maxLength: maxLength(50),
        noSpecialCharacters,
      },
      firstName2: {
        maxLength: maxLength(50),
        noSpecialCharacters,
      },
      lastName: {
        required,
        maxLength: maxLength(50),
        noSpecialCharacters,
      },
      email: {
        required,
        email,
        maxLength: maxLength(250),
      },
      phoneNumber: {
        telFormatIsValid,
        atLeastOnePhoneNumber,
      },
      mobilePhoneNumber: {
        telFormatIsValid,
        atLeastOnePhoneNumber,
      },
      birthDate: {},
      cityOfBirth: {
        required,
        maxLength: maxLength(50),
        noSpecialCharacters,
      },
      countryOfBirth: {
        required,
      },
      nationality: {
        required,
      },
      hasPaid: {},
      numDossier: {},
      address1: {
        required,
        maxLength: maxLength(250),
      },
      address2: {
        maxLength: maxLength(250),
      },
      postalCode: {
        required,
        frenchCpMustHave5Digits,
      },
      city: {
        required,
        maxLength: maxLength(50),
      },
      country: {
        required,
      },
      photoIdentity: {},
      cardIdentity: {},
      inscriptionDate: {},
      examSessionType: {
        required,
      },
      nameCardHolder: {
        required: requiredIf(function () {
          return !this.inscription.candidateIsCardHolder;
        }),
        maxLength: maxLength(250),
      },
      address1CardHolder: {
        required: requiredIf(function () {
          return !this.inscription.candidateIsCardHolder;
        }),
        maxLength: maxLength(250),
      },
      address2CardHolder: {},
      postalCodeCardHolder: {},
      cityCardHolder: {
        required: requiredIf(function () {
          return !this.inscription.candidateIsCardHolder;
        }),
        maxLength: maxLength(50),
      },
      countryCardHolder: {
        required: requiredIf(function () {
          return !this.inscription.candidateIsCardHolder;
        }),
      },
      phoneNumberCardHolder: {
        telFormatIsValid,
        required: requiredIf(function () {
          return (
            !this.inscription.candidateIsCardHolder &&
            (this.inscription.mobilePhoneNumberCardHolder === null ||
              this.inscription.mobilePhoneNumberCardHolder === '' ||
              this.inscription.mobilePhoneNumberCardHolder === undefined)
          );
        }),
      },
      mobilePhoneNumberCardHolder: {
        telFormatIsValid,
        required: requiredIf(function () {
          return (
            !this.inscription.candidateIsCardHolder &&
            (this.inscription.phoneNumberCardHolder === null ||
              this.inscription.phoneNumberCardHolder === '' ||
              this.inscription.phoneNumberCardHolder === undefined)
          );
        }),
      },
      emailCardHolder: {
        required: requiredIf(function () {
          return !this.inscription.candidateIsCardHolder;
        }),
        email,
        maxLength: maxLength(250),
      },
    },
    birthDateText: {
      required,
      dateFormatIsValid,
      haveMinAge,
      birthDateGreaterThan,
    },
    emailConfirmation: {
      required,
      sameAsEmail: sameAs(function () {
        return this.inscription.email;
      }),
      email,
    },
    checkboxCgv: {
      sameAs: sameAs(() => true),
    },
  },
})
export default class InscriptionForm extends mixins(JhiDataUtils, AlertMixin) {
  @Inject('inscriptionService') private inscriptionService: () => InscriptionService;
  @Inject('autocompletionLocationService') private autocompletionLocationService: () => AutocompletionLocationService;
  @Inject('jhiDataUtils') private jhiDataUtils: () => JhiDataUtils;
  @Inject('dateUtils') private dateUtils: () => DateUtils;
  @Inject('captchetatService') private captchetatService: () => CaptchetatService;
  @Inject('examSessionService') private examSessionService: () => ExamSessionService;
  @Inject('applicationService') private applicationService: () => ApplicationService;

  @Prop() public examSessionToRegister: ExamSession;

  @Ref('file_photoIdentity') readonly photoIdentityFile;
  @Ref('file_cardIdentity') readonly cardIdentityFile;

  public inscription: IInscription = new Inscription();

  public genderEnum = Object.keys(Gender);
  public countryEnum = Object.values(Country);
  public nationalityEnum = Object.values(Nationality);

  public registrationFee: string = '';

  // form : inputs and errors
  public birthDateText: string = '';
  public emailConfirmation: string = '';
  public checkboxCgv: boolean = false;

  public minFileSize: number = 10;
  public maxFileSize: number = 4000;
  public errorPhotoIdentityRequired: boolean = false;
  public errorPhotoIdentityMinSize: boolean = false;
  public errorPhotoIdentityMaxSize: boolean = false;
  public errorPhotoIdentityType: boolean = false;
  public errorCardIdentityRequired: boolean = false;
  public errorCardIdentityMinSize: boolean = false;
  public errorCardIdentityMaxSize: boolean = false;
  public errorCardIdentityType: boolean = false;

  public errorField: string = '';
  public errorFieldMessage: string = '';
  public globalErrorMessage: string = '';
  public showErrors: boolean = false;

  // autocompletion
  public searchedLocation: string = '';
  public enableAutocompletion: boolean = true;
  public autocompleteOnPostalCode: boolean = false;
  public autocompleteOnCity: boolean = false;

  // captchetat
  public captchaValidated: boolean = false;

  public isSaving = false;

  @Watch('examSessionToRegister')
  examSessionChanged(newSelectedExamSession: ExamSession) {
    this.examSessionToRegister = newSelectedExamSession;
    this.inscription.examSession = this.examSessionToRegister;
  }

  public mounted() {
    // get registration fee
    this.getRegistrationFee();
    // get min and max file size allowed (for identity photo and identity card)
    this.getMinMaxFileSize();
    // init default values in select inputs
    this.inscription.countryOfBirth = Country.FRA;
    this.inscription.country = Country.FRA;
    this.inscription.nationality = Nationality.FRANCE;
    this.inscription.countryCardHolder = Country.FRA;
  }

  /**
   * On form submit -> validate form. If validation ok -> save inscription
   */
  public submit() {
    this.isSaving = true;
    this.globalErrorMessage = '';
    if (!this.captchaValidated) {
      this.isSaving = false;
      this.showErrors = true;
      this.globalErrorMessage = 'Veuillez valider le captcha avant de procéder au paiement';
      return;
    }
    if (this.validateForm()) {
      this.showErrors = false;

      // Check if a previous payed registration exists
      this.checkPreviousRegistration().then(res => {
        let previousRegistrationExists = res;

        if (previousRegistrationExists) { // Open modal
          this.isSaving = false;
          if (<any>this.$refs.payedRegistrationExists) {
            (<any>this.$refs.payedRegistrationExists).show();
          }
        } else {
          this.save();
        }
      });
    } else {
      this.isSaving = false;
      this.showErrors = true;
      this.globalErrorMessage = ERR_FORM_INVALID;
    }
  }

  /**
   * Called when captchetat widget emits a validated event.
   */
  public onCaptchaValidated(isValid: boolean) {
    this.captchaValidated = isValid;
  }

  /**
   * Save inscription
   */
  public save(): void {
    this.isSaving = true;
    this.inscriptionService()
      .createPublic(this.inscription)
      .then(
        res => {
          this.$router.push({name: 'PaymentStart', params: {inscriptionId: res.id.toString()}});
        },
        err => {
          this.isSaving = false;
          this.closeDialogPayedRegistrationExists();
          if (err.response.data.errorKey === 'examSessionNotAvailable') {
            this.globalErrorMessage = err.response.data.message;
          } else if (err.response.data.errorKey === 'errorValidationInput') {
            this.errorField = err.response.data.field;
            this.errorFieldMessage = err.response.data.message;
            this.globalErrorMessage = ERR_FORM_INVALID;
          } else {
            this.globalErrorMessage = ERR_INSCRIPTION_FAILED;
          }
        }
      );
  }

  /**
   * Check if a previous payed registration exists with same firstName, lastName, email and examType.
   * Return true if previous registration exists.
   */
  async checkPreviousRegistration(): Promise<boolean> {
    this.isSaving = true;
    let previousRegistration = false;
    await this.inscriptionService()
      .checkPreviousRegistration(this.inscription).then(
        res => {
          previousRegistration = res;
        });
    return previousRegistration;
  }

  public closeDialogPayedRegistrationExists(): void {
    (<any>this.$refs.payedRegistrationExists).hide();
  }

  /**
   * Form validation
   */
  public validateForm(): boolean {
    let isValid = true;
    this.$v.$touch();
    const photoIdentity = this.photoIdentityFile.files[0];
    const cardIdentity = this.cardIdentityFile.files[0];
    this.validatePhotoIdentity(photoIdentity);
    this.validateCardIdentity(cardIdentity);
    if (this.$v.$invalid || !this.validatePhotoIdentity(photoIdentity) || !this.validateCardIdentity(cardIdentity)) {
      isValid = false;
    }
    return isValid;
  }

  /**
   * Validate file when input value changes
   */
  public validateFileOnChange(event, entity, field, isImage) {
    if (event && event.target.files && event.target.files[0]) {
      const file = event.target.files[0];
      if (field === 'photoIdentity') {
        if (this.validatePhotoIdentity(file)) {
          this.jhiDataUtils().setFileData(event, entity, field, isImage);
        }
      } else if (field === 'cardIdentity') {
        if (this.validateCardIdentity(file)) {
          this.jhiDataUtils().setFileData(event, entity, field, isImage);
        }
      }
    }
  }

  /**
   * Validate identity photo file
   * @param file
   */
  public validatePhotoIdentity(file): boolean {
    let isValid = true;
    // photoIdentity is required
    if (file === undefined || file === null) {
      this.errorPhotoIdentityRequired = true;
      isValid = false;
    } else {
      this.errorPhotoIdentityRequired = false;
      // photoIdentity size must be between min and max file size define in application.yml
      const fileSizeInKo = this.jhiDataUtils().getFileSizeInKo(file);
      if (fileSizeInKo > this.maxFileSize) {
        this.errorPhotoIdentityMaxSize = true;
        this.errorPhotoIdentityMinSize = false;
        isValid = false;
      } else if (fileSizeInKo < this.minFileSize) {
        this.errorPhotoIdentityMinSize = true;
        this.errorPhotoIdentityMaxSize = false;
        isValid = false;
      } else {
        this.errorPhotoIdentityMaxSize = false;
        this.errorPhotoIdentityMinSize = false;
      }
      // photoIdentity file must be in accepted MIME Types (const PHOTO_IDENTITY_MIME_TYPES)
      if (!PHOTO_IDENTITY_MIME_TYPES.includes(file.type)) {
        this.errorPhotoIdentityType = true;
        isValid = false;
      } else {
        this.errorPhotoIdentityType = false;
      }
    }
    return isValid;
  }

  /**
   * Validate identity card file
   * @param file
   */
  public validateCardIdentity(file): boolean {
    let isValid = true;
    // cardIdentity is required
    if (file === undefined || file === null) {
      this.errorCardIdentityRequired = true;
      isValid = false;
    } else {
      this.errorCardIdentityRequired = false;
      // cardIdentity size must be between min and max file size define in application.yml
      const fileSizeInKo = this.jhiDataUtils().getFileSizeInKo(file);
      if (fileSizeInKo > this.maxFileSize) {
        this.errorCardIdentityMaxSize = true;
        this.errorCardIdentityMinSize = false;
        isValid = false;
      } else if (fileSizeInKo < this.minFileSize) {
        this.errorCardIdentityMinSize = true;
        this.errorCardIdentityMaxSize = false;
        isValid = false;
      } else {
        this.errorCardIdentityMinSize = false;
        this.errorCardIdentityMaxSize = false;
      }
      // cardIdentity file must be in accepted MIME Types (const CARD_IDENTITY_MIME_TYPES)
      if (!CARD_IDENTITY_MIME_TYPES.includes(file.type)) {
        this.errorCardIdentityType = true;
        isValid = false;
      } else {
        this.errorCardIdentityType = false;
      }
    }
    return isValid;
  }

  /**
   * Get min and max file size allowed for identity photo and identity card
   */
  public getMinMaxFileSize() {
    this.applicationService()
      .getMinFileSize()
      .then(
        res => {
          this.minFileSize = res;
        }
      );
    this.applicationService()
      .getMaxFileSize()
      .then(
        res => {
          this.maxFileSize = res;
        }
      );
  }

  /**
   * Get registration fee (to be displayed with registration form)
   */
  public getRegistrationFee() {
    this.inscriptionService()
      .getRegistrationFee()
      .then(
        res => {
          this.registrationFee = String(res.data).slice(0, String(res.data).length - 2) + ',' + String(res.data).slice(-2) + ' €';
        }
      );
  }

  /**
   * When value of input "city" is updated, launch autocompletion with this value
   * (searchedLocation is used for autocompletion purpose (-> prop of child component autocompletionLocation))
   */
  public onChangeInputCity() {
    this.autocompleteOnCity = true;
    this.autocompleteOnPostalCode = false;
    this.searchedLocation = this.inscription.city;
  }

  /**
   * When value of input "postalCode" is updated, launch autocompletion with this value
   * (searchedLocation is used for autocompletion purpose (-> prop of child component autocompletionLocation))
   */
  public onChangeInputPostalCode() {
    this.autocompleteOnPostalCode = true;
    this.autocompleteOnCity = false;
    this.searchedLocation = this.inscription.postalCode;
  }

  /**
   * Enable or disable autocompletion based on selected country
   * If value of input "country" = "France" then activate autocompletion for candidate location.
   */
  public onChangeCountryInput() {
    if (this.inscription.country.toUpperCase() === Country.FRA.toUpperCase()) {
      this.enableAutocompletion = true;
    } else {
      this.enableAutocompletion = false;
    }
  }

  /**
   * Retrieve location selected by user emitted by child component autocompletionLocation
   * @param locationSelected result of autocompletion selected by user
   */
  public setSelectedLocation(locationSelected) {
    this.inscription.postalCode = locationSelected.postalCode;
    this.inscription.city = locationSelected.city;
  }

  /**
   * Set inscription.birthDate in Date format if string value is valid
   */
  public onChangeInputBirthDateText() {
    // TODO INCREMENT 2 : à retirer pour l'incrément 2
    if (this.$v.birthDateText.dateFormatIsValid) {
      this.inscription.birthDate = this.dateUtils().convertStringToDate(this.birthDateText);
    }

    // TODO INCREMENT 2 : à décommenter pour l'incrément 2
    // if (this.$v.birthDateText.dateFormatIsValid && this.$v.birthDateText.haveMinAge) {
    //   this.inscription.birthDate = this.dateUtils().convertStringToDate(this.birthDateText);
    // }
  }
}
